import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Calendar, ChevronDown } from 'lucide-react';
import { useYear } from '../contexts/YearContext';

function YearSelector() {
  const { selectedYear, setSelectedYear } = useYear();
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadAvailableYears();
  }, []);

  const loadAvailableYears = async () => {
    try {
      const response = await axios.get(`${API}/years/available`);
      const yearsWithData = response.data;
      
      // Add current year and next 2 years to ensure future years are available
      const currentYear = new Date().getFullYear();
      const allYears = new Set([...yearsWithData, currentYear, currentYear + 1, currentYear + 2]);
      
      // Convert to sorted array (newest first)
      const sortedYears = Array.from(allYears).sort((a, b) => b - a);
      setAvailableYears(sortedYears);
      
      // Year is managed by context, no need to set here
    } catch (err) {
      console.error('Error loading available years:', err);
      // Fallback to current year + next 2 years
      const currentYear = new Date().getFullYear();
      const fallbackYears = [currentYear + 2, currentYear + 1, currentYear];
      setAvailableYears(fallbackYears);
      // Year is managed by context
    } finally {
      setLoading(false);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
        <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400 dark:text-gray-400" />
        <span className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-300">Loading...</span>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="year-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
        data-testid="year-selector-btn"
      >
        <Calendar className="w-4 h-4" />
        <span className="font-medium">Year: {selectedYear || new Date().getFullYear()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-20">
            <div className="py-1">
              {availableYears.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearSelect(year)}
                  className={`w-full text-left px-4 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                    selectedYear === year ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-semibold' : 'text-gray-700 dark:text-gray-300'
                  }`}
                  data-testid={`year-option-${year}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{year}</span>
                    {selectedYear === year && (
                      <span className="text-blue-600">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            
            {availableYears.length === 0 && (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
                No data available
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default YearSelector;
